import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, MessageSquare, Repeat } from 'lucide-react';

/**
 * The frame the rest of the platform story hangs on: social care is short of
 * capacity, and three things compound against that.
 *
 * Opens the Platform page, and appears in a condensed row on the home page.
 * It used to sit on Technical Specifications, where the people it is written
 * for never went.
 *
 * Conservative on purpose. Wren is described by what it does — it reads, and
 * the one thing it creates is a task it will not create until a person
 * approves it. The scheduled agents are the ones that genuinely run on a
 * timer today: provider enrichment hourly, campaign steps continuously,
 * member communications every fifteen minutes, clinical signals daily.
 * Nothing here is a roadmap item written in the present tense.
 */
interface Layer {
  icon: typeof GraduationCap;
  eyebrow: string;
  title: string;
  short: string;
  body: string;
  proof: string;
}

const LAYERS: Layer[] = [
  {
    icon: GraduationCap,
    eyebrow: 'More people',
    title: 'Train them from the community they serve',
    short:
      'Courses, competencies and cohorts run on the platform, so an organization can credential someone who already knows the neighborhood.',
    body: 'Courses, competencies and cohorts run on the platform, so an organization can take someone who already knows the neighborhood and credential them — instead of competing for clinical hires who will never have that relationship.',
    proof:
      'Live: course delivery, competency tracking and cohort management, running with community health worker cohorts today.',
  },
  {
    icon: MessageSquare,
    eyebrow: 'More time each',
    title: 'Give every worker an assistant',
    short:
      'Wren answers questions about the work in plain language, in English or Spanish, from only what that person is allowed to see.',
    body: 'Wren answers questions about the work in plain language — who on my caseload has not been reached, where do I record a housing referral, open a follow-up for this member — in English or Spanish, and only from what that person is allowed to see.',
    proof:
      'Live, and deliberately narrow: Wren reads, and the one thing it can create is a task, which it refuses to do until a person approves it on screen. It cannot message a member or change a record.',
  },
  {
    icon: Repeat,
    eyebrow: 'Work that never stops',
    title: 'Agents that keep going between visits',
    short:
      'They do not wait to be asked. The directory is enriched, outreach advances, communications sync and signals are swept on their own schedule.',
    body: 'The rest of the work does not wait to be asked for. Persistent agents run on their own schedule: the provider directory is enriched hourly, outreach steps advance on time, member communications sync every fifteen minutes, and clinical signals are swept daily — so a worker returns to a caseload that moved while they were out.',
    proof:
      'Live: scheduled agents running from every few minutes to weekly, on the same tool catalogue a person uses, under the same permissions.',
  },
];

interface CapacityStoryProps {
  /** Condensed single row, for the home page. */
  compact?: boolean;
}

const CapacityStory: React.FC<CapacityStoryProps> = ({ compact = false }) => {
  if (compact) {
    return (
      <section className="py-16 bg-white dark:bg-gray-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-ink mb-3">
              Social care is not short of good intentions.
              <br className="hidden sm:block" />
              <span className="text-v2accent"> It is short of capacity.</span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              There are not enough community health workers, and the ones who exist spend a
              large part of the week on work that is not care. WeaveIn moves on three
              things at once.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {LAYERS.map(({ icon: Icon, eyebrow, title, short }) => (
              <div
                key={title}
                className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6"
              >
                <Icon className="w-6 h-6 text-v2accent mb-3" />
                <p className="text-xs font-bold tracking-[0.14em] uppercase text-v2accent mb-1.5">
                  {eyebrow}
                </p>
                <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">{title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {short}
                </p>
              </div>
            ))}
          </div>

          <p className="text-center mt-8">
            <Link
              to="/platform"
              className="text-sm font-semibold text-v2accent hover:underline"
            >
              See how it works on the Platform page
            </Link>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-20 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold tracking-[0.16em] uppercase text-v2accent mb-4">
          Why it is built this way
        </p>
        <h2 className="text-3xl md:text-4xl font-bold text-ink mb-5 leading-tight">
          Social care is not short of good intentions.
          <br />
          <span className="text-v2accent">It is short of capacity.</span>
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
          There are not enough community health workers, and the ones who exist spend a
          large part of the week on work that is not care. You cannot close the loop on a
          referral if nobody has the hours to make the call, and you cannot hire your way
          out of it at the rates this work pays.
        </p>
        <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-10">
          So WeaveIn moves on three things at once. Each one alone is an improvement.
          Together they multiply.
        </p>

        <ol className="space-y-5">
          {LAYERS.map(({ icon: Icon, eyebrow, title, body, proof }) => (
            <li
              key={title}
              className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-6 md:p-8"
            >
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 flex-none rounded-xl bg-v2accent-soft flex items-center justify-center">
                  <Icon className="w-6 h-6 text-ink" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold tracking-[0.14em] uppercase text-v2accent mb-1.5">
                    {eyebrow}
                  </p>
                  <h3 className="text-xl font-bold text-ink mb-2">
                    {title}
                  </h3>
                  <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-3">
                    {body}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed border-l-2 border-v2accent pl-4">
                    {proof}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ol>

        <p className="mt-8 text-base text-gray-700 dark:text-gray-200 leading-relaxed">
          <strong className="font-semibold">
            A trained worker who is not doing paperwork is the unit of capacity.
          </strong>{' '}
          Train more of them, give each one back the hours, and let the scheduled work run
          itself — and a region serves materially more people with the organizations it
          already has. That is also why the loop can close: recording what happened finally
          costs the person who did it almost nothing.
        </p>
      </div>
    </section>
  );
};

export default CapacityStory;
